import { RefObject, useCallback } from 'react';

interface InsertPlaceholderOptions {
  /** Placeholder name, inserted as `{param}`. */
  param: string;
  /** Current text of the edited field. */
  text: string;
  onChange: (value: string) => void;
  /** Textarea the caret position is read from; may be null when unmounted. */
  el: HTMLTextAreaElement | null;
  /** Clamps the resulting text when the field has a length limit. */
  maxLength?: number;
}

const clamp = (value: string, maxLength?: number): string =>
  maxLength === undefined ? value : value.slice(0, maxLength);

/**
 * Inserts a `{param}` token at the caret (replacing the selection) and restores
 * the caret right after the inserted token. Falls back to appending when the
 * textarea is not available.
 */
export const insertPlaceholderIntoText = ({
  param,
  text,
  onChange,
  el,
  maxLength,
}: InsertPlaceholderOptions): void => {
  const token = `{${param}}`;
  if (el) {
    const start = el.selectionStart ?? text.length;
    const end = el.selectionEnd ?? text.length;
    const next = clamp(text.slice(0, start) + token + text.slice(end), maxLength);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      const caret = Math.min(start + token.length, next.length);
      el.setSelectionRange(caret, caret);
    });
  } else {
    onChange(clamp(text + token, maxLength));
  }
};

/**
 * Caret-aware placeholder insertion for a single textarea. Returns the
 * `insertPlaceholder(param)` handler wired to the given text/ref.
 */
export const useInsertPlaceholder = (
  text: string,
  onChange: (value: string) => void,
  textareaRef: RefObject<HTMLTextAreaElement | null>,
  maxLength?: number
): ((param: string) => void) =>
  useCallback(
    (param: string) =>
      insertPlaceholderIntoText({
        param,
        text,
        onChange,
        el: textareaRef.current,
        maxLength,
      }),
    [text, onChange, textareaRef, maxLength]
  );
